import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Statistic, Spin, Alert, Typography, Drawer, Table, Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import MonthlyScorecard from './MonthlyScorecard';
import SkillGroupTag from './SkillGroupTag';

/**
 * How the campaign is doing now.
 *
 * The periods are fixed -- month to date, last month, this week, yesterday --
 * and deliberately not driven by a range picker. These cards answer "where are
 * we", and a picker would make them answer something different on every visit.
 *
 * A missing figure is a dash, never a zero. Several of these come from separate
 * queries against two databases that can fail or be empty independently, and
 * "no evaluations yet" is not the same answer as "0%".
 */

const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const secs = (v) => {
  if (v == null) return '—';
  const m = Math.floor(v / 60);
  const sec = Math.round(v % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};
const num = (v) => (v == null ? '—' : Number(v).toLocaleString());

const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};
const fmtDay = (iso) => {
  const [, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}`;
};

// Platinum through Below, in the order they should be read.
const BANDS = [
  { key: 'Below',    label: 'Below Silver', color: '#cf1322' },
  { key: 'Silver',   label: 'Silver',       color: '#8c8c8c' },
  { key: 'Gold',     label: 'Gold',         color: '#d48806' },
  { key: 'Platinum', label: 'Platinum',     color: '#1677ff' },
];

function Figure({ label, value, color, sub, onClick }) {
  return (
    <div style={{ minWidth: 92 }}>
      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{label}</Typography.Text>
      <Typography.Text
        strong
        style={{
          fontSize: 20,
          color,
          ...(onClick ? { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 3 } : null),
        }}
        onClick={onClick}
      >{value}</Typography.Text>
      {sub && (
        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{sub}</Typography.Text>
      )}
    </div>
  );
}

/**
 * An SLA figure coloured against its own period's target. No target means no
 * colour -- green and red would be claiming a verdict there is no basis for.
 */
function SLAFigure({ label, slot, headerTarget }) {
  const { pct: value, target } = slot ?? {};
  const color = value == null || target == null
    ? undefined
    : value >= target ? '#389e0d' : '#cf1322';
  return (
    <Figure
      label={label}
      value={pct(value)}
      color={color}
      // Only when this period's target is not the one in the heading. A month
      // judged against a different number should say so rather than look
      // inconsistent with the figure beside it.
      sub={target != null && headerTarget != null && target !== headerTarget
        ? `target ${pct(target)}`
        : undefined}
    />
  );
}

/** One card: a title and a row of figures that wrap on a narrow screen. */
function Group({ title, extra, children }) {
  return (
    <Card size="small" title={title} extra={extra} style={{ height: '100%' }} styles={{ body: { padding: '10px 12px' } }}>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>{children}</div>
    </Card>
  );
}

/**
 * The agent band counts. Null means no goal is set for the metric, which is
 * said out loud -- four zeroes would read as everyone failing.
 */
function Bands({ bands, members, onOpen, metric }) {
  if (!bands) {
    return <Typography.Text type="secondary" style={{ fontSize: 11 }}>No goal set, so no bands</Typography.Text>;
  }
  return (
    <>
      {BANDS.map((b) => {
        // Only a band with anybody in it is worth opening. A dotted underline
        // on a zero would promise a list and then show an empty drawer.
        const rows = members?.[b.key] ?? [];
        return (
          <Figure
            key={b.key}
            label={`Agents ${b.label}`}
            value={num(bands[b.key])}
            color={b.color}
            onClick={rows.length ? () => onOpen(`${metric} — ${b.label}`, rows) : undefined}
          />
        );
      })}
    </>
  );
}

/**
 * Who is in one band.
 *
 * The list comes from the same response as the count above it rather than a
 * second query, so the two cannot disagree about who is in the band. Ordered
 * worst first, since the reason to open Below Silver is to find who needs
 * attention.
 */
function BandDrawer({ open, title, rows, onClose }) {
  const navigate = useNavigate();
  return (
    <Drawer title={title} open={open} onClose={onClose} width={480}>
      {rows?.length ? (
        <Table
          rowKey={(r) => r.UserID ?? r.Name}
          size="small"
          pagination={false}
          dataSource={rows}
          columns={[
            {
              title: 'Agent', dataIndex: 'Name', key: 'Name',
              render: (v, r) => r.UserID
                ? <Typography.Link onClick={() => navigate(`/agents?user_id=${encodeURIComponent(r.UserID)}`)}>{v}</Typography.Link>
                : v,
            },
            {
              title: 'Value', dataIndex: 'Value', key: 'Value',
              align: 'right', width: 110,
              render: (v) => v == null ? '—' : `${Number(v).toFixed(1)}%`,
            },
          ]}
        />
      ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nobody in this band" />}
    </Drawer>
  );
}

export default function OverviewTab({ campaignId, teamId, skillGroup, skills, can = {} }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed]   = useState(false);
  const [band, setBand]       = useState(null);   // { title, rows }

  useEffect(() => {
    if (!campaignId) { setData(null); return; }
    setLoading(true);
    setFailed(false);
    api.get('/api/campaign-summary/scorecards', {
      params: { campaign_id: campaignId, team_id: teamId || undefined, skill: skills?.join(',') || undefined },
    })
      .then((r) => setData(r.data && typeof r.data === 'object' ? r.data : null))
      .catch(() => { setData(null); setFailed(true); })
      .finally(() => setLoading(false));
  }, [campaignId, teamId, skills]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }
  if (failed) {
    return <Alert type="error" showIcon message="The scorecards could not be loaded." style={{ marginTop: 16 }} />;
  }
  if (!data) return null;

  const { sla, evals, errors, coaching, adherence, aht, attrition, absence_hours: absHrs } = data;
  // Only Raw SLA follows a Skill Group; every other card says it does not.
  const skillTag = (relevant) => <SkillGroupTag group={skillGroup} relevant={relevant} />;

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} xl={14}>
          {/* The target sits beside the heading, like AHT to Goal. Each
              period carries its own, because targets change and judging last
              month against this month's would misreport the month it moved. */}
          <Group
            title={(
              <span>
                Raw SLA
                {sla?.mtd?.target != null && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }}>
                    target {pct(sla.mtd.target)}
                  </Typography.Text>
                )}
              </span>
            )}
            extra={skillTag(true)}
          >
            <SLAFigure label="MTD"        slot={sla?.mtd}        headerTarget={sla?.mtd?.target} />
            <SLAFigure label="Last Month" slot={sla?.last_month} headerTarget={sla?.mtd?.target} />
            <SLAFigure label="This Week"  slot={sla?.this_week}  headerTarget={sla?.mtd?.target} />
            {/* Named by its date when that is not actually yesterday.
                DailyCallLogSum loads on a lag, so the most recent day with
                data is often the day before -- worth showing, but not worth
                mislabelling. */}
            <SLAFigure
              label={sla?.yesterday_date && sla.yesterday_date !== yesterdayISO()
                ? `Last Day (${fmtDay(sla.yesterday_date)})`
                : 'Yesterday'}
              slot={sla?.yesterday}
              headerTarget={sla?.mtd?.target}
            />
          </Group>
        </Col>
        {can.adherence && (
        <Col xs={24} xl={10}>
          <Group title="Adherence" extra={skillTag(false)}>
            <Figure label="MTD"        value={pct(adherence?.mtd)} />
            <Figure label="Last Month" value={pct(adherence?.last_month)} />
            {/* The selected team's absence hours (Absences access only). */}
            {absHrs && <Figure label="Absence Notification Hrs MTD" value={Number(absHrs.mtd).toFixed(1)} />}
            {absHrs && <Figure label="Absence Notification Hrs Last Month" value={Number(absHrs.last_month).toFixed(1)} />}
          </Group>
        </Col>
        )}
      </Row>

      {can.evals && (
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24}>
          <Group title="Evals" extra={skillTag(false)}>
            <Figure label="Avg Eval % MTD"        value={pct(evals?.mtd)} />
            <Figure label="Avg Eval % Last Month" value={pct(evals?.last_month)} />
            <Bands bands={evals?.bands} members={evals?.band_members} metric="QA Score"
                   onOpen={(title, rows) => setBand({ title, rows })} />
          </Group>
        </Col>
      </Row>
      )}

      {can.errors && (
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24}>
          <Group title="Errors" extra={skillTag(false)}>
            <Figure label="Error % MTD"        value={pct(errors?.mtd)} />
            <Figure label="Error % Last Month" value={pct(errors?.last_month)} />
            <Bands bands={errors?.bands} members={errors?.band_members} metric="Error %"
                   onOpen={(title, rows) => setBand({ title, rows })} />
          </Group>
        </Col>
      </Row>
      )}

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24}>
          {/* The goal sits in the title rather than as card extra. Extra is
              flush right, which on a full-width card puts the number the
              figures are measured against about a metre from them. */}
          <Group
            title={(
              <span>
                AHT to Goal
                {aht?.mtd?.target != null && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }}>
                    goal {secs(aht.mtd.target)}
                  </Typography.Text>
                )}
              </span>
            )}
            extra={skillTag(false)}
          >
            {!aht?.mtd && !aht?.last_month ? (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>No AHT goal set</Typography.Text>
            ) : (
              <>
                <Figure label="MTD Agents Below"         value={num(aht?.mtd?.below)}         color="#cf1322" />
                <Figure label="MTD Agents Meeting"       value={num(aht?.mtd?.meeting)}       color="#389e0d" />
                <Figure label="Last Month Agents Below"  value={num(aht?.last_month?.below)}   color="#cf1322" />
                <Figure label="Last Month Agents Meeting"value={num(aht?.last_month?.meeting)} color="#389e0d" />
              </>
            )}
          </Group>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        {can.coaching && (
        <Col xs={24} xl={10}>
          <Group title="Coaching" extra={skillTag(false)}>
            <Figure label="Recoaching MTD"        value={num(coaching?.mtd)} />
            <Figure label="Recoaching Last Month" value={num(coaching?.last_month)} />
          </Group>
        </Col>
        )}
        {can.recruitment && (
        <Col xs={24} xl={10}>
          {/* Team only. People leave a team, not a campaign, and nothing in
              the data joins the two -- so without a team this says why it is
              empty rather than showing a company-wide number. */}
          <Group title="Attrition" extra={skillTag(false)}>
            {attrition ? (
              <>
                <Figure
                  label="MTD"
                  value={num(attrition.mtd?.total)}
                  sub={`Agents: ${num(attrition.mtd?.agents)}`}
                />
                <Figure
                  label="Last Month"
                  value={num(attrition.last_month?.total)}
                  sub={`Agents: ${num(attrition.last_month?.agents)}`}
                />
              </>
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Choose a team — attrition is recorded against a team, not a campaign
              </Typography.Text>
            )}
          </Group>
        </Col>
        )}
      </Row>

      {/* Its own window, not the page's. The range picker is hidden on this
          tab, and a table silently governed by a control you cannot see is
          worse than one with a window of its own that it states. */}
      {skillGroup && <div style={{ marginTop: 12 }}><SkillGroupTag group={skillGroup} relevant={false} /></div>}
      <MonthlyScorecard campaignId={campaignId} />

      <BandDrawer
        open={!!band}
        title={band?.title}
        rows={band?.rows}
        onClose={() => setBand(null)}
      />
    </>
  );
}
